import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, fmtDate, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Sun } from "lucide-react";

export default function QuotationInvoice() {
  const { id } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [co, setCo] = useState(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("mrm_token") : null;

  useEffect(() => {
    api.get(`/quotations/${id}`).then(({ data }) => setQ(data));
    api.get("/settings/company").then(({ data }) => setCo(data)).catch(() => {});
  }, [id]);

  if (!q) return <div className="p-10 text-center text-slate-400">Loading...</div>;

  const kw = q.system_size_kw || 0;
  const systemCost = q.system_cost ?? q.subtotal ?? 0;
  const gstPct = q.gst_percent || 8.9;
  const gstAmt = q.gst_amount ?? Math.round(systemCost * gstPct / 100);
  const finalCost = q.total ?? (systemCost + gstAmt - (q.subsidy || 0) - (q.discount || 0));

  const item = (no, desc, hsn, qty, rate, amount) => (
    <tr className="border-b border-slate-200" key={no}>
      <td className="py-2.5 px-2 text-sm text-slate-600 w-10">{no}</td>
      <td className="py-2.5 text-sm text-slate-800">{desc}</td>
      <td className="py-2.5 text-sm text-slate-600 font-mono">{hsn}</td>
      <td className="py-2.5 text-sm text-slate-600 text-center">{qty}</td>
      <td className="py-2.5 text-sm text-slate-600 text-right font-mono">{fmtINR(rate)}</td>
      <td className="py-2.5 text-sm text-slate-900 text-right font-mono font-semibold">{fmtINR(amount)}</td>
    </tr>
  );

  const sumRow = (label, val, cls = "") => (
    <tr className={`border-b border-slate-200 ${cls}`}>
      <td className="py-2.5 text-sm font-medium">{label}</td>
      <td className="py-2.5 text-sm text-right font-mono">{val}</td>
    </tr>
  );

  return (
    <div className="min-h-screen bg-slate-100 py-8">
      <div className="no-print max-w-4xl mx-auto flex justify-between items-center mb-4 px-4">
        <Button variant="outline" onClick={() => nav(-1)}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>
        <Button className="bg-amber-500 hover:bg-amber-600 text-white" onClick={() => window.print()} data-testid="print-invoice-btn"><Printer className="w-4 h-4 mr-1" />Print / Save PDF</Button>
      </div>

      <div className="max-w-4xl mx-auto bg-white p-8 sm:p-12 shadow-lg print-page" data-testid="invoice-doc">
        <div className="text-center text-base font-bold tracking-[0.35em] text-slate-800 border-b-2 border-slate-800 pb-2 mb-5">TAX INVOICE</div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between gap-4 mb-6">
          <div className="flex items-start gap-4">
            {co?.logo_url ? (
              <img src={co.logo_url} alt="MRM" className="h-16 object-contain" data-testid="invoice-logo" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center"><Sun className="w-8 h-8 text-white" strokeWidth={2.5} /></div>
            )}
            <div>
              <div className="text-lg font-extrabold text-slate-900 leading-tight" style={{fontFamily:'Outfit'}}>{co?.company_name || "MRM RENEWABLES ENERGY PRIVATE LIMITED"}</div>
              {co?.address && <div className="text-[11px] text-slate-600 max-w-sm mt-0.5 leading-snug">{co.address}</div>}
              <div className="text-[11px] text-slate-600 mt-0.5">Tel: {co?.phone || "-"} · Email: {co?.email || "-"}</div>
              <div className="text-[11px] font-semibold text-slate-800 mt-0.5">
                GSTIN: {co?.gstin || "-"}{co?.cin && <span className="font-normal text-slate-600"> · CIN: {co.cin}</span>}{co?.pan && <span className="font-normal text-slate-600"> · PAN: {co.pan}</span>}
              </div>
              {co?.udyam && <div className="text-[11px] text-slate-600">Udyam No.: {co.udyam}</div>}
            </div>
          </div>
          <div className="text-right shrink-0 border border-slate-200 rounded-lg p-3 bg-slate-50">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Invoice No.</div>
            <div className="text-xl font-bold font-mono text-slate-900">{q.quotation_no}</div>
            <div className="text-[10px] text-slate-500 mt-1">Invoice Date: {fmtDate(q.created_at)}</div>
            <div className="text-[10px] text-slate-500">State: Rajasthan (08)</div>
          </div>
        </div>

        {/* Bill To */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          <div className="border border-slate-200 rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Bill To</div>
            <div className="text-base font-bold text-slate-900">{q.customer_name}</div>
            <div className="text-sm text-slate-600">{q.customer_mobile}</div>
            {q.customer_address && <div className="text-sm text-slate-600">{q.customer_address}</div>}
            {q.customer_city && <div className="text-sm text-slate-600">{q.customer_city}</div>}
          </div>
          <div className="border border-slate-200 rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">System / Place of Supply</div>
            <div className="text-base font-bold text-amber-700">{kw} kW On-Grid Solar System</div>
            <div className="text-sm text-slate-600">{q.panel_brand} {q.panel_type || ""} · {q.panel_wattage}</div>
            <div className="text-sm text-slate-600">Inverter: {q.inverter_brand} {q.inverter_capacity}</div>
            <div className="text-sm text-slate-600">Place of Supply: Rajasthan (08)</div>
          </div>
        </div>

        {/* Items */}
        <table className="w-full mb-4 border-t-2 border-b-2 border-slate-800">
          <thead>
            <tr className="border-b border-slate-300 bg-slate-50">
              <th className="text-left py-2 px-2 text-[11px] uppercase tracking-wider text-slate-600">#</th>
              <th className="text-left py-2 text-[11px] uppercase tracking-wider text-slate-600">Description of Goods / Services</th>
              <th className="text-left py-2 text-[11px] uppercase tracking-wider text-slate-600">HSN/SAC</th>
              <th className="text-center py-2 text-[11px] uppercase tracking-wider text-slate-600">Qty</th>
              <th className="text-right py-2 text-[11px] uppercase tracking-wider text-slate-600">Rate</th>
              <th className="text-right py-2 text-[11px] uppercase tracking-wider text-slate-600">Amount</th>
            </tr>
          </thead>
          <tbody>
            {item(1, `${kw} kW On-Grid Solar Rooftop System — ${q.panel_brand} ${q.panel_type || ""} panels (${q.panel_wattage}), ${q.inverter_brand} inverter (${q.inverter_capacity}), ${q.structure_type || "HDGI"} structure, DC/AC cables, earthing, lightning arrester, ACDB/DCDB, complete installation & commissioning incl. net metering liaison`, "8541", "1 Lot", systemCost, systemCost)}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end mb-6">
          <div className="w-full sm:w-80">
            <table className="w-full">
              <tbody>
                {sumRow("Taxable Amount", fmtINR(systemCost))}
                {sumRow(`GST @ ${gstPct}%`, fmtINR(gstAmt))}
                {(q.subsidy || 0) > 0 && sumRow("Less: Subsidy (PM Surya Ghar)", `- ${fmtINR(q.subsidy)}`, "text-emerald-700")}
                {(q.discount || 0) > 0 && sumRow("Less: Discount", `- ${fmtINR(q.discount)}`, "text-emerald-700")}
                <tr className="bg-slate-900 text-white">
                  <td className="py-3 px-3 font-bold text-sm">GRAND TOTAL</td>
                  <td className="py-3 px-3 text-right font-mono font-bold text-lg">{fmtINR(finalCost)}</td>
                </tr>
              </tbody>
            </table>
            <div className="text-[10px] text-slate-500 mt-2 italic">Amount in words: Rupees {finalCost.toLocaleString("en-IN")} Only</div>
          </div>
        </div>

        {/* Bank */}
        {(co?.bank_name || co?.account_no) && (
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 mb-6" data-testid="invoice-bank-details">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Bank Details</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-700">
              {co.bank_name && <div><span className="text-slate-500">Bank:</span> {co.bank_name}</div>}
              {co.branch && <div><span className="text-slate-500">Branch:</span> {co.branch}</div>}
              {co.account_no && <div><span className="text-slate-500">Account No:</span> <span className="font-mono">{co.account_no}</span></div>}
              {co.ifsc && <div><span className="text-slate-500">IFSC:</span> <span className="font-mono">{co.ifsc}</span></div>}
            </div>
          </div>
        )}

        {/* Declaration + Sign */}
        <div className="grid grid-cols-2 gap-6 mt-6 pt-5 border-t border-slate-200">
          <div className="text-[11px] text-slate-600 space-y-1">
            <div className="font-semibold text-slate-800 text-xs uppercase tracking-wider">Declaration</div>
            <p>We declare that this invoice shows the actual price of the goods/services described and that all particulars are true and correct. Subject to Jaipur, Rajasthan jurisdiction.</p>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 mb-2">For {co?.company_name || "MRM Renewables"}</div>
            {q.created_by_signature && (
              <img src={`${API}/files/${q.created_by_signature}?auth=${token}`} alt="Signature" className="h-14 object-contain ml-auto mb-1" data-testid="invoice-sales-signature"/>
            )}
            <div className="border-t border-slate-400 inline-block pt-2 px-6">
              <div className="text-sm font-semibold text-slate-800" data-testid="invoice-sales-name">{q.created_by_name || "Authorised Signatory"}</div>
              <div className="text-[10px] text-slate-500">Sales Representative</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
